import { useNotify } from '@newfront-insurance/next-notifications';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getErrorMessage } from '../../lib/error-message';

/** Keep one removal in flight per surface and ignore completions after access or identity changes. */
export function useSettingsRemoval(scope: string, permitted: boolean, userUuid: string) {
  const notify = useNotify();
  const session = useMemo(() => ({ scope, userUuid, permitted }), [scope, userUuid, permitted]);
  const latest = useRef(session);
  const active = useRef(true);
  const pending = useRef<object | null>(null);
  const [state, setState] = useState<{ session: object; pendingKey: string | null; error?: string }>();
  useEffect(() => {
    latest.current = session;
  }, [session]);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const run = async <T>(
    key: string,
    mutate: () => Promise<T>,
    onSuccess: (value: T) => void | Promise<void>,
    message: string,
  ) => {
    const isCurrent = () => active.current && latest.current === session && permitted;
    if (!userUuid || !isCurrent() || pending.current === session) return false;
    pending.current = session;
    setState({ session, pendingKey: key });
    try {
      const value = await mutate();
      if (!isCurrent()) return false;
      const completion = onSuccess(value);
      if (completion) {
        await completion;
        if (!isCurrent()) return false;
      }
      notify.success(message);
      return true;
    } catch (error) {
      if (isCurrent())
        setState({
          session,
          pendingKey: null,
          error: getErrorMessage(error, 'Unable to complete this action. Try again.'),
        });
      return false;
    } finally {
      if (pending.current === session) pending.current = null;
      if (isCurrent())
        setState((current) => (current?.session === session ? { ...current, pendingKey: null } : current));
    }
  };

  return {
    run,
    clearError: () =>
      setState((current) => (current?.session === session ? { ...current, error: undefined } : current)),
    pendingKey: state?.session === session ? state.pendingKey : null,
    error: state?.session === session ? state.error : undefined,
  };
}
